import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { formatCurrency, formatCompactCurrency } from "@/utils/shared/currencyUtils";
import type { BusinessDashboardRecord } from "@/hooks/admin/BusinessDashboardTab/useBusinessDashboardMetrics";

interface BusinessIncomeExpensesChartProps {
  incomeData: Array<{ month: string; amount: number; records: BusinessDashboardRecord[] }>;
  expensesData: Array<{ month: string; amount: number; records: BusinessDashboardRecord[] }>;
  onBarClick: (month: string, incomeRecords: BusinessDashboardRecord[], expensesRecords: BusinessDashboardRecord[]) => void;
}

const BusinessIncomeExpensesChart = ({ incomeData, expensesData, onBarClick }: BusinessIncomeExpensesChartProps) => {
  // Combine data for the chart
  const combinedData = incomeData.map((income, index) => ({
    month: income.month,
    income: income.amount,
    expenses: expensesData[index]?.amount || 0,
    incomeRecords: income.records,
    expensesRecords: expensesData[index]?.records || [],
  }));

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: "hsl(var(--card))",
          border: "1px solid hsl(var(--border))",
          borderRadius: "8px",
          padding: "12px",
          boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)"
        }}>
          <p className="font-medium text-sm mb-2">{payload[0].payload.month}</p>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-success" />
              <span className="text-xs text-muted-foreground">Ingresos:</span>
              <span className="text-sm font-semibold text-success">
                {formatCurrency(payload[0].payload.income)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-muted-foreground" />
              <span className="text-xs text-muted-foreground">Egresos:</span>
              <span className="text-sm font-semibold text-muted-foreground">
                {formatCurrency(payload[0].payload.expenses)}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const handleBarClick = (data: any) => {
    if (data && data.incomeRecords !== undefined && data.expensesRecords !== undefined) {
      onBarClick(data.month, data.incomeRecords, data.expensesRecords);
    }
  };

  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={combinedData} margin={{ top: 20, right: 0, left: 0, bottom: 5 }} barGap={6}>
        <CartesianGrid 
          strokeDasharray="3 3" 
          stroke="hsl(var(--border))" 
          opacity={0.3}
        />
        <XAxis 
          dataKey="month" 
          angle={-45}
          textAnchor="end"
          height={70}
          interval={0}
          axisLine={{ stroke: "hsl(var(--border))" }}
          tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
        />
        <YAxis 
          width={45}
          tickFormatter={(value) => formatCompactCurrency(value)}
          axisLine={{ stroke: "hsl(var(--border))" }}
          tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
        />
        <Tooltip 
          content={<CustomTooltip />}
          cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
        />
        <Bar 
          dataKey="income" 
          fill="hsl(var(--success))"
          onClick={handleBarClick}
          cursor="pointer"
          radius={[4, 4, 0, 0]}
          barSize={20}
        />
        <Bar 
          dataKey="expenses" 
          fill="hsl(var(--border))"
          onClick={handleBarClick}
          cursor="pointer"
          radius={[4, 4, 0, 0]}
          barSize={20}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default BusinessIncomeExpensesChart;
